import type { IframePoolItem } from '../../../modules/api';
import { formatPrice } from '../../../modules/currency';
import { Image } from '../../../modules/design-system';

export type HitCardSize = 'large' | 'small';

type Props = {
  item: IframePoolItem;
  size: HitCardSize;
  onClick: (item: IframePoolItem) => void;
};

/** Desktop card sizes (repackz-handoff §8.1): the top tier 283×432 on a
 *  blurred glass tile, the rest 185×284 on a lighter one. */
const SIZES = {
  large: {
    tile: 'aspect-[283/432] rounded-[24px] p-3 [backdrop-filter:blur(var(--glass-panel-blur))]',
    slab: 'aspect-[259/408] rounded-[12px]',
    price: 'bottom-3',
    width: 259,
    height: 408,
  },
  small: {
    tile: 'aspect-[185/284] rounded-[16px] p-2.5',
    slab: 'aspect-[165/264] rounded-[6px]',
    price: 'bottom-2',
    width: 165,
    height: 264,
  },
} as const;

/** A potential hit on the desktop: the slab on a glass tile, its exact
 *  value on a dark plate at the bottom. */
export function HitCard({ item, size, onClick }: Props) {
  const s = SIZES[size];
  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      aria-label={item.title}
      className={
        `relative block w-full cursor-pointer overflow-hidden border-[0.5px] border-solid ${s.tile} ` +
        '[border-color:var(--glass-panel-rim)] [background:var(--glass-panel-fill)] transition-[filter] hover:brightness-110'
      }
    >
      <Image
        src={item.imageUrl}
        alt=""
        className={`relative w-full overflow-hidden ${s.slab}`}
        imgClassName="absolute inset-0 h-full w-full object-contain"
        width={s.width}
        height={s.height}
      />
      {item.itemPrice > 0 && (
        <span
          className={
            `absolute left-1/2 flex h-8 -translate-x-1/2 items-center whitespace-nowrap rounded-full px-3 ${s.price} ` +
            'border-[0.5px] border-solid text-[15px] font-semibold text-white backdrop-blur-[12px] ' +
            '[background:var(--glass-price-fill)] [border-color:var(--glass-price-rim)] [box-shadow:0_4px_24px_rgba(0,0,0,0.25)]'
          }
        >
          {formatPrice(item.itemPrice, 2)}
        </span>
      )}
    </button>
  );
}
